import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { AssessmentReportJTFWhenOptions } from '@utils/Variables'
import { handlePrint } from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { Col, Flex, Row } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Entities_Login_StateInfo_Response } from '@suntex.fim/api-client'
import { JTFPlayerGraph } from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $ArithmeticOperation, $JtfMode, JtfMode } from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalPlayerContext } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { LevelSelect, LoaderOverlay } from '@suntex.fim/ui'

import { appSessionStorage } from '../../utils/storageUtils'

import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'

const jtfLevelOptions = $JtfMode.listOptions()
const operationOptions = $ArithmeticOperation.listOptions()
const jtfWhenOptions = objToLevSelOpts(AssessmentReportJTFWhenOptions)

function capitalizeFirstLetter(text: string | undefined): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}
/**
 * Returns JSX for just the fact report
 */
function AssessmentReportJTFByPlayer() {
  useRotateDevice()
  const { api } = useFramework()
  const { jwt, login } = useAuth(isAuthenticated)
  const team = useOptionalTeamInfo()

  // Use strongly typed query string parser
  const {
    jtfLevel,
    jtfOperation,
    jtfWhen,
    playerId: playerIdFromQueryString
  } = useAppQueryString()
  const playerContext = useOptionalPlayerContext()
  const playerId = playerContext?.playerId ?? playerIdFromQueryString

  // Destructure session storage values for easier access
  const {
    jtfLevel: sessionJtfLevel,
    jtfOperation: sessionJtfOperation,
    jtfWhen: sessionJtfWhen
  } = appSessionStorage

  const [isExporting, setIsExporting] = useState(false)

  // Initialize filters with proper fallbacks using strongly typed query params
  const [filters, setFilters] = useState(() => ({
    level: $JtfMode.from(jtfLevel ?? sessionJtfLevel ?? JtfMode.WholeNumbers),
    operation: $ArithmeticOperation.from(
      jtfOperation ?? sessionJtfOperation ?? jtfLevelOptions[0].key
    ),
    when: jtfWhen ?? sessionJtfWhen ?? jtfWhenOptions[0].key
  }))

  // Optimized filter update functions
  const updateLevel = useCallback((level: string | number) => {
    setFilters((prev) => ({ ...prev, level: $JtfMode.from(Number(level)) }))
  }, [])

  const updateOperation = useCallback((operation: string | number) => {
    setFilters((prev) => ({
      ...prev,
      operation: $ArithmeticOperation.from(Number(operation))
    }))
  }, [])

  const updateWhen = useCallback((when: string | number) => {
    setFilters((prev) => ({ ...prev, when: Number(when) }))
  }, [])

  // Memoize print handler to avoid recreation on every render
  const handlePrintClick = useCallback(() => {
    handlePrint({
      setIsLoading: setIsExporting,
      fileName: `${playerId}_jtf`
    })
  }, [playerId])

  const { data: player, isLoading: isLoadingPlayer } = useQuery({
    queryKey: ['assessment', 'playerSummary', playerId],
    queryFn: () =>
      api.get_Assessment_PlayerSummary({
        playerId: playerId!
      }),
    enabled: playerId != null
  })

  const { data: jtfProgress = [], isLoading: isLoadingProgress } = useQuery({
    queryKey: [
      'assessment',
      'jtfProgress',
      playerId,
      filters.level,
      filters.operation,
      filters.when
    ],
    queryFn: async () => {
      const response = await api.get_Player_JustTheFactsPlayerProgress({
        playerId: playerId!,
        jtfMode: filters.level,
        arithmeticOperation: filters.operation,
        prePostMode: filters.when
      })
      return response.factMatrix || []
    },
    enabled: playerId != null
  })

  useEffect(() => {
    // Update session storage
    appSessionStorage.jtfLevel = filters.level
    appSessionStorage.jtfOperation = filters.operation
    appSessionStorage.jtfWhen = filters.when
  }, [filters.level, filters.operation, filters.when])

  const isLoading = isLoadingPlayer || isLoadingProgress || isExporting

  // Memoize stats rendering
  const statsSection = useMemo(() => {
    const labels = ['Region', 'District', 'School', 'Team']
    return (
      <div className="main-navigation__stats">
        {labels.map((label) => {
          const key =
            `${label.toLowerCase()}Name` as keyof Entities_Login_StateInfo_Response
          return (
            <div key={label} className="stat">
              <span className="label">{label}</span>
              <span className="numbers">
                {capitalizeFirstLetter(
                  (label === 'Team'
                    ? player?.teamName
                    : (login?.[key] as string)) || ''
                )}
              </span>
            </div>
          )
        })}
      </div>
    )
  }, [login, team, player])
  if (isLoading) {
    return <LoaderOverlay isLoading={isLoading} />
  }
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="6px" style={{ position: 'relative' }}>
        <Row gutter={[10, 10]} className="main-navigation" align="middle">
          <Col xs={{ flex: '55%' }} md={{ flex: 'auto' }}>
            <Flex
              gap="10px"
              className="main-navigation__heading"
              align="center"
            >
              <h3>Pre/Post Test Data - Just The Facts</h3>
            </Flex>
          </Col>
          <Col
            flex="auto"
            className={'main-navigation__btn-container print-hidden'}
          >
            <div className="export-btn">
              <Button
                shape="round"
                intent="secondary"
                onClick={handlePrintClick}
              >
                View Printer Friendly Version
              </Button>
            </div>
          </Col>
          <Col flex="100%">{statsSection}</Col>
        </Row>
        <Row gutter={[10, 6]} className="main-navigation report-filter">
          <Col>
            <Flex gap={14} wrap>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={filters.level}
                  onChange={updateLevel}
                  options={jtfLevelOptions}
                />
              </Flex>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Operations</p>
                <LevelSelect
                  value={filters.operation}
                  onChange={updateOperation}
                  options={operationOptions}
                />
              </Flex>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">When</p>
                <LevelSelect
                  value={filters.when}
                  onChange={updateWhen}
                  options={jtfWhenOptions}
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
        <AssessmentReportSummaryDashboard isPlayer player={player} />
        <div className="main-dashboard">
          <JTFPlayerGraph
            jtfProgress={jtfProgress}
            mode={filters.level}
            operation={filters.operation}
          />
        </div>
      </Flex>
    </>
  )
}

export default AssessmentReportJTFByPlayer
